import { makeAutoObservable } from 'mobx'
import { toastStore } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import { type IFullWorkflowData, type IWorkflow, WorkflowsApi } from 'entities/Workflow'
import { transformWorkflowV1TriggerArgsToV2 } from 'entities/Workflow/utils/transformWorkflowV1TriggerArgsToV2'
import { ActivateWorkflowModalStore } from 'pages/workflows/ui/ActivateWorkflowModal'
import type { IParamsActivateWorkflowModalStore } from 'pages/workflows/ui/ActivateWorkflowModal/store/ActivateWorkflowModalStore'

export class WorkflowOperationStore {
  private _deleteModalId = 'deleteWorkflowModal'

  constructor() {
    makeAutoObservable(this)
  }

  duplicate = async (workflow: IWorkflow) => {
    try {
      const newWorkflow = {
        ...workflow.baseData,
        name: `Copy of ${workflow.name}`,
      }

      if (newWorkflow.name.length > 100) {
        toastStore.add({
          title: 'Failed to duplicate workflow. Name should be no more than 100 characters',
          type: 'error',
        })

        return null
      }

      const {
        data: { payload },
      } = await WorkflowsApi.createWorkflow(newWorkflow)

      return payload
    } catch (error) {
      toastStore.add({
        title: 'Something went wrong. Please try again.',
        type: 'error',
      })

      logger.error(error)

      return null
    }
  }

  enable = async (workflow: IWorkflow) => {
    let resolvePublish: ((value: IFullWorkflowData | null) => void) | null = null
    const promisePublish = new Promise<IFullWorkflowData | null>((resolve) => {
      resolvePublish = resolve
    })
    let payload: IFullWorkflowData | null = null
    const onPublish: IParamsActivateWorkflowModalStore['onPublish'] = async ({
      isEnrollExist,
      isReEnroll,
    }) => {
      try {
        const res = await WorkflowsApi.updateWorkflow(workflow.id, {
          ...workflow.baseData,
          multiple_executions: isReEnroll,
        })
        payload = res.data.payload

        await WorkflowsApi.enableWorkflow(payload?.id)

        toastStore.add({
          type: 'info',
          title: 'Workflow activated',
        })

        resolvePublish?.({ ...payload, enabled: true })

        if (isEnrollExist && payload.id) {
          WorkflowsApi.enrollIntoWorkflow(
            {
              apply_trigger_filters: true,
            },
            payload.id
          ).catch(logger.error)
        }
      } catch (error) {
        toastStore.add({
          title: 'Complete set-up of workflow actions, or remove empty actions, before publishing',
          type: 'error',
        })
        resolvePublish?.(null)

        logger.error(error)
      }
    }

    new ActivateWorkflowModalStore({
      filters: transformWorkflowV1TriggerArgsToV2(workflow?.v2Data.definition.trigger.args)
        ?.filters,
      onPublish,
      onCancel: () => {
        resolvePublish?.(null)
      },
    })

    return await promisePublish
  }

  disable = async (workflow: IWorkflow) => {
    try {
      await WorkflowsApi.disableWorkflow(workflow.id)

      toastStore.add({
        title: 'Workflow disabled',
        type: 'info',
      })

      return { ...workflow.baseData, enabled: false }
    } catch (error) {
      toastStore.add({
        title: 'Something went wrong. Please try again.',
        type: 'error',
      })

      logger.error(error)

      return null
    }
  }

  delete = (workflow: IWorkflow) =>
    new Promise<boolean>((resolve) => {
      const handleDelete = () => {
        modalStore.removeModal(this._deleteModalId)
        WorkflowsApi.deleteWorkflow(workflow.id).then(
          () => {
            toastStore.add({
              title: 'Workflow deleted',
              type: 'success',
            })
            resolve(true)
          },
          () => {
            toastStore.add({
              title: 'Something went wrong. Please try again.',
              type: 'error',
            })
            resolve(false)
          }
        )
      }

      const handleCancel = () => {
        modalStore.removeModal(this._deleteModalId)
        resolve(false)
      }

      modalStore.addModal({
        id: this._deleteModalId,
        showHeader: true,
        showCloseButton: false,
        showCloseIcon: true,
        width: 280,
        type: ModalTypeList.ALERT,
        zIndex: ZIndex.OVERLAY_MIDDLE,
        title: 'Delete workflow?',
        desc: 'This action cannot be undone',
        primaryAction: {
          text: 'Delete',
          onAction: handleDelete,
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: handleCancel,
        },
        onClose: handleCancel,
      })
    })
}

export const workflowOperation = new WorkflowOperationStore()
